import {
  FormControl,
  FormLabel,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Stack,
  Tooltip,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { FormState } from "../types";

import { isDuplicate } from "./utils";

interface CensusGroupClassesProps {
  maxNumberOfClasses: number;
  classNames: string[];
  updateFormStateValue: <T extends keyof FormState>(
    key: T,
    value: FormState[T],
  ) => void;
}

const CensusGroupClasses = (props: CensusGroupClassesProps) => {
  const theme = useTheme();
  const { maxNumberOfClasses, classNames, updateFormStateValue } = props;

  const handleNumberOfClassesChange = (value: number) => {
    let newClassNames = [...classNames];

    if (value === 1) {
      newClassNames = ["Class 1"];
    } else if (value < classNames.length) {
      newClassNames = newClassNames.slice(0, value);
    } else {
      if (classNames.length === 1) {
        newClassNames[0] = "";
      }
      for (let i = classNames.length; i < value; i++) {
        newClassNames.push("");
      }
    }
    updateFormStateValue("censusGroupClasses", newClassNames);
  };

  const handleClassNameChange = (index: number, value: string) => {
    const newClassNames = [...classNames];
    newClassNames[index] = value;
    updateFormStateValue("censusGroupClasses", newClassNames);
  };

  return (
    <>
      <Box display="flex" flexDirection="column" px={2} mb={3} maxWidth="80%">
        <FormLabel sx={{ marginBottom: 2 }} required>
          How many classes does this group have?
        </FormLabel>
        <FormControl sx={{ width: "50%" }}>
          <InputLabel id="number-of-classes-label">
            Number of Classes
          </InputLabel>
          <Select
            labelId="number-of-classes-label"
            id="number-of-classes-select"
            value={classNames.length > 0 ? classNames.length : ""}
            label="Number of Classes"
            type="number"
            onChange={(e) =>
              handleNumberOfClassesChange(Number(e.target.value))
            }
          >
            {Array.from({ length: maxNumberOfClasses }, (_, i) => i + 1).map(
              (number) => (
                <MenuItem key={number} value={number}>
                  {number}
                </MenuItem>
              ),
            )}
          </Select>
        </FormControl>
      </Box>

      {classNames.length > 1 && (
        <Box px={2} mb={3} display="flex" flexDirection="column" maxWidth="80%">
          <FormLabel required>
            Please provide a Class Description for each Class Number below—{" "}
          </FormLabel>
          <Typography variant="caption" fontStyle="italic" fontSize={16}>
            These values must align with the values on the RFP upload document
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "200px" }}>
                  <Typography variant="smallBold">Class Number</Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography variant="smallBold">
                      Class Description
                    </Typography>
                    <Tooltip title="Add the Class Description here as displayed  in the census to map it to a Class Number.">
                      <InfoOutlinedIcon
                        sx={{ fontSize: theme.spacing(2) }}
                        cursor="pointer"
                      />
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {classNames.map((value, index) => (
                <TableRow key={index}>
                  <TableCell>Class {index + 1}</TableCell>
                  <TableCell>
                    <TextField
                      name={`class-${index + 1}`}
                      sx={{ marginTop: 1 }}
                      variant="outlined"
                      label="Add the class description as it appears on the census"
                      required
                      fullWidth
                      value={value}
                      onChange={(e) =>
                        handleClassNameChange(index, e.target.value)
                      }
                      error={isDuplicate(classNames, value, index)}
                      helperText={
                        isDuplicate(classNames, value, index) &&
                        "Duplicate class descriptions not allowed"
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </>
  );
};

export default CensusGroupClasses;
